import { useQuery } from '@apollo/client';
import React, { ReactNode, useEffect } from 'react';

import { AppointmentSection, H3 } from './Appointments.styled';
import ActionBanner from './components/ActionBanner';
import { Column, Row } from '../../styled/Grid';
import { AppointmentCard } from 'components/AppointmentCard/AppointmentCard';
import { Heading } from 'components/v2/Typography/Heading';
import { usePatient } from 'hooks/usePatient/usePatient';
import { Appointment } from 'kb-shared';
import { PATIENT_APPOINTMENTS } from 'kb-shared/graphql/queries';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { ResponsiveContainer } from 'screens/styled_common';
import { analytics } from 'utilities/analytics';

import { PatientAppointmentsResponse } from './Appointments.types';
import { getFutureAppointments, getPastAppointments } from './Appointments.utils';

export const Appointments: React.FC = () => {
  const { patient } = usePatient();
  const { data } = useQuery<PatientAppointmentsResponse>(PATIENT_APPOINTMENTS, {
    onError: error => BugTracker.notify(error, 'PatientAppointments-Appointments.tsx')
  });
  const scheduledAppointments = getFutureAppointments(data);
  const pastAppointments = getPastAppointments(data);

  const renderAppointments = (appointments: Appointment[]): ReactNode | null => {
    return appointments.map(appointment => (
      <Column key={appointment.id} $mobile={12} $tablet={6} $desktop={4}>
        <AppointmentCard
          appointment={appointment}
          type={appointment.appointmentType}
          onAppointmentCancelled={() => window.location.reload()}
          patient={patient}
        />
      </Column>
    ));
  };

  const numAppointmentsBooked = scheduledAppointments && scheduledAppointments.length;
  const numPastAppointments = pastAppointments && pastAppointments.length;

  useEffect(() => {
    analytics.page(analytics.PAGES.APPOINTMENTS);
  }, []);

  return (
    <ResponsiveContainer $paddedContent>
      <Heading>Appointments</Heading>

      <ActionBanner />
      {!!scheduledAppointments && !!numAppointmentsBooked && (
        <AppointmentSection>
          <H3>
            {numAppointmentsBooked} upcoming appointment{numAppointmentsBooked > 1 && 's'}:
          </H3>
          <Row>{renderAppointments(scheduledAppointments)}</Row>
        </AppointmentSection>
      )}
      {!!pastAppointments && !!numPastAppointments && (
        <AppointmentSection>
          <H3>
            {numPastAppointments} past appointment{numPastAppointments > 1 && 's'}:
          </H3>
          <Row>{renderAppointments(pastAppointments)}</Row>
        </AppointmentSection>
      )}
    </ResponsiveContainer>
  );
};
